/**
 * Live amount input mask. Groups the integer part with the locale thousands separator as the user types and
 * keeps an in-progress decimal, while parsing a clean numeric value. Idempotent (re-masking already-formatted
 * text is a no-op) and paste-friendly: any non-numeric characters and stray thousands separators are dropped,
 * so pasting "1.234.567,89" or "1,234,567.89" resolves to the same number for the matching locale config.
 */
export interface AmountMaskConfig {
  /** Thousands separator used for grouping (e.g. "." for COP/BRL, "," for MXN). */
  thousandSep: string;
  /** Decimal separator, or null for integer-only currencies (COP). */
  decimalSep: string | null;
  /** Max fractional digits kept (0 for COP, 2 for BRL/MXN). */
  maxDecimals: number;
}

export const COP_MASK: AmountMaskConfig = { thousandSep: ".", decimalSep: null, maxDecimals: 0 };
export const BRL_MASK: AmountMaskConfig = { thousandSep: ".", decimalSep: ",", maxDecimals: 2 };
export const MXN_MASK: AmountMaskConfig = { thousandSep: ",", decimalSep: ".", maxDecimals: 2 };

export interface MaskedAmount {
  /** Formatted text to show in the input. */
  display: string;
  /** Parsed numeric value, or null when empty / not greater than zero. */
  value: number | null;
}

/** Nothing typed yet — the neutral seed for a masked-amount lens. */
export const EMPTY_MASKED_AMOUNT: MaskedAmount = { display: "", value: null };

/** Strips leading zeros (keeping a single 0) and groups digits in threes. */
const groupInteger = (digits: string, sep: string): string => {
  const trimmed = digits.replace(/^0+(?=\d)/, "");
  return trimmed.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
};

export const maskAmount = (input: string, cfg: AmountMaskConfig): MaskedAmount => {
  // Integer-only currencies: keep digits, group, done.
  if (cfg.decimalSep === null) {
    const digits = input.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
    if (digits === "") return { display: "", value: null };
    const value = Number(digits);
    return { display: groupInteger(digits, cfg.thousandSep), value: value > 0 ? value : null };
  }

  const dec = cfg.decimalSep;
  // Keep only digits and the locale decimal separator; thousands seps and any
  // other characters are dropped so the field never shows non-numeric input.
  let cleaned = "";
  for (const ch of input) {
    if (ch >= "0" && ch <= "9") cleaned += ch;
    else if (ch === dec) cleaned += dec;
  }

  const firstDec = cleaned.indexOf(dec);
  const hasDecimal = firstDec !== -1;
  let intDigits = (hasDecimal ? cleaned.slice(0, firstDec) : cleaned).replace(/\D/g, "");
  const decDigits = hasDecimal
    ? cleaned
        .slice(firstDec + 1)
        .replace(/\D/g, "")
        .slice(0, cfg.maxDecimals)
    : "";

  intDigits = intDigits.replace(/^0+(?=\d)/, "");
  if (intDigits === "" && !hasDecimal) return { display: "", value: null };
  if (intDigits === "") intDigits = "0"; // typed ".5" -> "0.5"

  const grouped = groupInteger(intDigits, cfg.thousandSep);
  const display = hasDecimal ? `${grouped}${dec}${decDigits}` : grouped;
  const value = Number(`${intDigits}.${decDigits || "0"}`);
  return { display, value: value > 0 ? value : null };
};

export const ARS_MASK: AmountMaskConfig = { thousandSep: ".", decimalSep: ",", maxDecimals: 2 };

const MASK_BY_CURRENCY: Readonly<Record<string, AmountMaskConfig>> = {
  COP: COP_MASK,
  BRL: BRL_MASK,
  MXN: MXN_MASK,
  ARS: ARS_MASK,
};

/**
 * Mask for a currency, so one shared amount field can serve every corridor. Unlisted currencies fall back to
 * the Latin-American grouping with cents — never to an integer mask, which would silently swallow the
 * decimals the user typed.
 */
export const maskForCurrency = (currency: string): AmountMaskConfig =>
  MASK_BY_CURRENCY[currency.toUpperCase()] ?? BRL_MASK;

/**
 * Seeds a masked field from an API decimal string (`"754540.00000000"`, always `.`-separated), at the
 * currency's own precision: the fraction is cut to `maxDecimals` and its trailing zeros dropped, so a figure
 * read back from a quote reads like one the person typed.
 */
export const maskDecimalString = (value: string, cfg: AmountMaskConfig): MaskedAmount => {
  const [integer = "", fraction = ""] = value.trim().split(".");
  const digits = integer.replace(/\D/g, "");
  const decimals = cfg.decimalSep
    ? fraction.replace(/\D/g, "").slice(0, cfg.maxDecimals).replace(/0+$/, "")
    : "";
  return maskAmount(decimals ? `${digits}${cfg.decimalSep}${decimals}` : digits, cfg);
};
